import React, {useEffect, useState} from 'react';
import getSymbolFromCurrency from 'currency-symbol-map'
import numeral from 'numeral';
import classnames from 'classnames';
import List from "../../../../components/List/List";
import ListItem from "../../../../components/List/ListItem";
import ListItemCell from "../../../../components/List/ListItemCell";
import RegionTitle from "../../../../components/typography/RegionTitle";
import Button from "../../../../../src/components/buttons/Button";
import FileDrop from '../../../../components/Forms/FileDrop';
import { Spinner } from '../../../../components';
import './BulkLocationEditorMenus.scss';
import { uploadMenu, getMenu } from './MenusApi';
import presignedExport from "../../../../services/auth/presignedExport";
import modalService from "../../../../components/modal/modalService"
import { Confirm } from "../../../../components";

let csvAcceptType = "text/csv"

const downloadMenuApi = (id) => presignedExport.downloadExport(`/v5/listingManagement/foodMenu/${id}/download`);
const downloadMenuExample = () => presignedExport.downloadExport(`/v5/listingManagement/foodMenu/template.csv`);

const menuToSummary = (menu) => ({
  "name": menu.labels[0].displayName,
  "sectionCount": menu.sections.length,
  "itemCount": menu.sections.flatMap(section => section.items).length
});

function BulkLocationEditorMenus ({
  setFormIsInvalid,
  singleLocationMode,
  setFoodMenuIdsOnPatch,
  foodMenuId,
  setFoodMenuSummary
}) {
  const multiLocationMode = !singleLocationMode;

  const initialMenusState = {
    loading: false,
    menu: null,
    activeMenuSectionIndex: 0,
    errors: null
  }

  const [menuState, setMenuState] = useState({...initialMenusState});

  const handleMenuUpload = (file) => {
    setMenuState({...menuState, loading: true, menu: null});
    const formData = new FormData();
    formData.append(
      "newMenu",
      file,
      file.name
    );

    uploadMenu(formData).then(resp => {
      onMenuUploadSuccess(resp);
    })
    .catch(error => {
      onMenuUploadFailure(error.response.data);
    });
  }

  const onMenuUploadSuccess = (menu) => {
    setMenuState({...menuState, activeMenuSectionIndex: 0, menu, errors: null, loading: false});
    setFoodMenuIdsOnPatch([menu._id]);
    setFoodMenuSummary(menuToSummary(menu));
  }

  const onMenuUploadFailure = (response) => {
    setMenuState({...menuState, activeMenuSectionIndex: 0, menu: null, errors: response.errors, loading: false});
    setFoodMenuIdsOnPatch([]);
    setFoodMenuSummary(null)
    const errors = <ul>
      {response.errors.map(error => {
        return <li key={error}>{error}</li>
      })}
    </ul>;

    modalService.requestModalV2({ modal: <Confirm
          visible={true}
          toggle={modalService.closeModal}
          header="Import Error"
          message={<>
            <div>
              Could not upload menu, please fix the following
            </div>
            <br/>
            {errors}
            <br/>
            <br/>
            Hint: You can download a menu template to use as a guide.
          </>
          }
          showCancelButton={false}
          confirmText="Close"
          onContinue={async () => {}}
      />})
  }

  const downloadMenu = () => {
    downloadMenuApi(menu._id);
  }

  const clearMenu = () => {
    setMenuState({...initialMenusState})
    setFoodMenuIdsOnPatch([]);
    setFoodMenuSummary(null);
  }

  useEffect(() => {
    if (!!foodMenuId) {
      setMenuState({ ...menuState, loading: true});
      getMenu(foodMenuId)
        .then(menu => {
          setMenuState({ ...menuState, activeMenuSectionIndex: 0, menu, errors: null, loading: false })
        });
    }
  }, []);

  const { loading, menu, activeMenuSectionIndex } = menuState;
  // Stretch the second column to full width if we are hiding the first column when no menu is uploaded
  const secondColumnSize =  !!menu ? " col-sm-9 col-md-10" : " col-sm-12";

  return <div className="food-menu-container">
    <div className="row">
      <div className="col-sm-12 col-md-12 d-flex">
        {!!menu ? ("Last imported: " + (new Date(menu.createdAt)).toLocaleDateString()) :
            <span style={{"opacity": "0"}}>I{/* Invisible spacer to prevent line shifting */}</span>}
      </div>
    </div>
    <div className="row">
        {!!menu && <div className="col-sm-3 col-md-2 d-flex align-items-end">
        <div className="d-flex">
          <RegionTitle smTitle>Sections</RegionTitle>
        </div>
      </div>}
      <div className={"d-flex align-items-center" + secondColumnSize}>
        <div className="d-flex flex-row align-items-end w-100">
          <RegionTitle smTitle>{!!menu && "Menu Items"}</RegionTitle>
          {!!menu ? <div className="d-flex align-items-center ml-auto">
              <Button outline className={"control-spacer-left"} onClick={downloadMenu}>Download Menu</Button>
              <Button outline className={"control-spacer-left"} onClick={clearMenu}>Clear Menu</Button>
              <Button outline className={"control-spacer-left"} onClick={downloadMenuExample}>Download Template</Button>
              <FileDrop
                maxSize={10000000} // TODO: How much?
                onFileAdded={files => {
                  clearMenu();
                  handleMenuUpload(files[0]);
                }}
                accepts={csvAcceptType}
              >
              <Button outline className={"control-spacer-left"}>Import Menu</Button>
            </FileDrop>
            </div> : <div className="d-flex align-items-center ml-auto">
              <Button outline className={"control-spacer-left"} onClick={downloadMenuExample}>Download Template</Button>
            </div>
          }
        </div>
      </div>
    </div>
    <div className="row card-spacer-top">
      {!!menu && <div className="col-sm-3 col-md-2 d-flex align-items-start">
        <div className="selectable-col d-flex flex-column h-100 w-100 border">
          {!!menu && menu.sections.map((menuSection, index) => {
            return <div className={"selectable-row p-2" + (index === activeMenuSectionIndex ? " selected" : "" )} onClick={() => setMenuState({ ...menuState, activeMenuSectionIndex: index })}>{menuSection.labels[0].displayName}</div>
          })}
        </div>
      </div>}
      <div className={"d-flex align-items-start overflow-auto" + secondColumnSize}>
        {!!menu ? <List className="border">
          {menu.sections[activeMenuSectionIndex].items.map((menuItem, index) => {
            return <ListItem className={`card-padding ${index < menu.sections[activeMenuSectionIndex].items.length - 1 ? "border-bottom" : ""}`}>
              <ListItemCell className={`foodMenuItemImage d-flex align-items-center justify-content-center ${!!menuItem.attributes.mediaUrls[0] ? "bg-dark rounded": ""}`}>
                <img src={!!menuItem.attributes.mediaUrls[0] ? menuItem.attributes.mediaUrls[0] : require("../../../../assets/images/menu-no-image.png")} className="img-fluid p-0 border-0 rounded" style={{ height: "70px" }} />
              </ListItemCell>
              <ListItemCell className="flex-1 card-padding-left" style={{minHeight: "90px"}}><p className="font-weight-bold mb-0">
                {menuItem.labels[0].displayName}</p><p className={classnames({ "mb-3": !!menuItem.options.length })}>{menuItem.labels[0].description}</p>
                {!!menuItem.options.length && menuItem.options.map((opt, index2) => <div key={`option-${index}-${index2}`}>{opt.labels[0].displayName}</div>)}
              </ListItemCell>
              <ListItemCell className="foodMenuItemDetail d-flex justify-content-center card-padding-left font-weight-bold"><br />{!!menuItem.attributes.nutritionFacts && !!menuItem.attributes.nutritionFacts.calories ? `${menuItem.attributes.nutritionFacts.calories.lowerAmount} ${menuItem.attributes.nutritionFacts.calories.unit}s` : ""}</ListItemCell>
              <ListItemCell className="foodMenuItemDetail d-flex justify-content-center card-padding-left font-weight-bold"><br />{!!menuItem.attributes.price && !!menuItem.attributes.price.currencyCode && !!menuItem.attributes.price.units ? `${getSymbolFromCurrency(menuItem.attributes.price.currencyCode)}${numeral(menuItem.attributes.price.units).format("0,0.00")}` : ""}</ListItemCell>
            </ListItem>
          })}
        </List> : <FileDrop
          containerClassName="d-flex h-100 w-100"
          maxSize={10000000} // TODO: How much?
          onFileAdded={files => handleMenuUpload(files[0])}
          accepts={csvAcceptType}
        >
          <div className="d-flex flex-column align-items-center justify-content-center border card-padding flex-1">
            {
              loading
              ?
              <div className="d-flex align-items-center justify-content-center" style={{
                height: "205px"
              }}>
                <Spinner timeout={0} size="md" message={`${!!menu ? "Updating" : "Importing"} Menu`}/>
              </div>
              :
              <div className="d-flex flex-column align-items-center justify-content-center" style={{
                height: "205px"
              }}>
                <p>
                  No menu has been uploaded for this location
                </p>
                <p>
                  <i className="far fa-cloud-upload fa-5x"></i>
                </p>
                <p>
                  Drag and drop a .CSV file
                </p>
                <p>
                  or
                </p>
                <Button className="px-5">Browse</Button>
              </div>
            }
          </div>
        </FileDrop>}
      </div>
    </div>
  </div>
}

export default BulkLocationEditorMenus;
